import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextAppwrite';
import { 
  LayoutDashboard, 
  Users, 
  TestTube, 
  Package, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  HelpCircle
} from 'lucide-react';
import Icon from './AppIcon';


const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/users', label: 'Users', icon: Users },
    { path: '/samples', label: 'Samples', icon: TestTube },
    { path: '/dewars', label: 'Dewars', icon: Package },
    { path: '/reports', label: 'Reports', icon: FileText },
    { path: '/settings', label: 'Settings', icon: Settings },
    { path: '/help', label: 'Help', icon: HelpCircle },
  ];

  const isActive = (path) => location?.pathname === path;

  return (
    <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Desktop Navigation */}
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-blue-600">Cryo Manager</span>
                <span className="text-xs text-gray-400 font-normal">v{__APP_VERSION__}</span>
              </Link>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:ml-6 md:flex md:space-x-1">
              {navItems?.map((item) => {
                const Icon = item?.icon;
                return (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`inline-flex items-center whitespace-nowrap flex-shrink-0 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive(item?.path)
                        ? 'bg-blue-50 text-blue-700' :'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item?.label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* User Menu and Mobile Toggle */}
          <div className="flex items-center flex-shrink-0">
            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              className="hidden md:inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </button>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* User Info Mobile */}
            <div className="px-3 py-2 text-sm border-b border-gray-200 mb-2">
              <p className="font-medium text-gray-900">
                {profile?.full_name || user?.email}
              </p>
              {profile?.role === 'admin' && (
                <span className="mt-1 inline-block px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                  Admin
                </span>
              )}
            </div>

            {/* Mobile Navigation Items */}
            {navItems?.map((item) => {
              const Icon = item?.icon;
              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-3 py-2 text-base font-medium rounded-md ${
                    isActive(item?.path)
                      ? 'bg-blue-50 text-blue-700' :'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item?.label}
                </Link>
              );
            })}

            {/* Mobile Sign Out */}
            <button
              onClick={() => {
                handleSignOut();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center px-3 py-2 text-base font-medium text-gray-700 hover:text-red-600 hover:bg-gray-50 rounded-md"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;